import React from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { MarketData, MarketComparison as MarketComparisonType } from '../types';
import { MARKET_DATA } from '../utils/constants';
import { formatCurrency, formatPercentage, calculateAffordabilityScore } from '../utils/calculations';

interface MarketComparisonProps {
  comparison: MarketComparisonType;
  onComparisonChange: (comparison: MarketComparisonType) => void;
  userIncome?: number;
}

const MarketComparison: React.FC<MarketComparisonProps> = ({
  comparison,
  onComparisonChange,
  userIncome = 0
}) => {
  // Available markets for selection - sorted alphabetically by state, then by city
  const availableMarkets = MARKET_DATA.sort((a, b) => {
    // First sort by state
    const stateCompare = a.state.localeCompare(b.state);
    if (stateCompare !== 0) return stateCompare;
    // Then sort by city name within same state
    return a.cityName.localeCompare(b.cityName);
  });

  const handleMarketSelect = (marketIndex: 1 | 2 | 3, market: MarketData) => {
    const key = `market${marketIndex}` as keyof MarketComparisonType;
    onComparisonChange({
      ...comparison,
      [key]: market
    });
  };

  const clearMarket = (marketIndex: 1 | 2 | 3) => {
    const key = `market${marketIndex}` as keyof MarketComparisonType;
    onComparisonChange({
      ...comparison,
      [key]: undefined
    });
  };

  // Calculate scores for each market if user income is provided
  const getMarketScore = (market: MarketData) => {
    if (!userIncome || userIncome <= 0) return null;
    
    // Simplified affordability calculation
    const adjustedIncome = userIncome * (1 + market.tradeSalaryPremium);
    const maxAffordable = (adjustedIncome * 0.30 * 12) / 0.065; // Rough estimate
    const remainingBudget = adjustedIncome - (market.starterHomePrice * 0.065 / 12);
    
    return calculateAffordabilityScore(maxAffordable, market.medianHomePrice, remainingBudget, adjustedIncome);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <FaMapMarkerAlt className="text-blue-600 text-xl" style={{ color: '#1e3a8a' }} />
        <h2 className="text-2xl font-semibold text-gray-900">Market Research Guide</h2>
      </div>

      {/* Market Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {[1, 2, 3].map((index) => {
          const marketKey = `market${index}` as keyof MarketComparisonType;
          const selectedMarket = comparison[marketKey];
          
          return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">City {index}</h3>
              
              {/* Market Selector */}
              <div className="mb-4">
                <label className="label">Target Market Name</label>
                <select
                  className="input-field"
                  style={{ backgroundColor: '#bfdbfe' }}
                  value={selectedMarket?.cityName || ''}
                  onChange={(e) => {
                    const market = availableMarkets.find(m => m.cityName === e.target.value);
                    if (market) {
                      handleMarketSelect(index as 1 | 2 | 3, market);
                    } else if (e.target.value === '') {
                      clearMarket(index as 1 | 2 | 3);
                    }
                  }}
                >
                  <option value="">Select a market</option>
                  {availableMarkets.map((market) => (
                    <option key={`${market.cityName}-${market.state}`} value={market.cityName}>
                      {market.cityName}, {market.state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Market Data Display */}
              {selectedMarket && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Median Home Price:</span>
                      <span className="font-medium" style={{ color: '#1e3a8a' }}>
                        {formatCurrency(selectedMarket.medianHomePrice)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Starter Home (25th %):</span>
                      <span className="font-medium" style={{ color: '#16a34a' }}>
                        {formatCurrency(selectedMarket.starterHomePrice)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Property Tax Rate:</span>
                      <span className="font-medium">
                        {formatPercentage(selectedMarket.propertyTaxRate)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">State Income Tax:</span>
                      <span className="font-medium">
                        {formatPercentage(selectedMarket.stateIncomeTaxRate)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trade Wages vs National Avg:</span>
                      <span className="font-medium" style={{ 
                        color: selectedMarket.tradeSalaryPremium > 0.02 ? '#16a34a' : 
                               selectedMarket.tradeSalaryPremium < -0.02 ? '#dc2626' : 
                               '#6b7280'
                      }}>
                        {selectedMarket.tradeSalaryPremium >= 0 ? '+' : ''}{formatPercentage(selectedMarket.tradeSalaryPremium)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cost of Living Index:</span>
                      <span className="font-medium">
                        {selectedMarket.costOfLivingIndex.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Market Score */}
                  {userIncome > 0 && (
                    <div className="mt-3 p-2 bg-gray-50 rounded border">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Affordability Score:</span>
                        <span className="font-bold text-lg" style={{ 
                          color: getMarketScore(selectedMarket)! > 7 ? '#16a34a' : 
                                getMarketScore(selectedMarket)! > 4 ? '#f59e0b' : '#dc2626' 
                        }}>
                          {getMarketScore(selectedMarket)?.toFixed(1)}/10
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Side-by-Side Comparison */}
      {(comparison.market1 || comparison.market2 || comparison.market3) && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Side-by-Side Comparison</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                  <th className="text-left p-3 font-medium">Comparison Metrics</th>
                  {comparison.market1 && (
                    <th className="text-center p-3 font-medium">{comparison.market1.cityName}</th>
                  )}
                  {comparison.market2 && (
                    <th className="text-center p-3 font-medium">{comparison.market2.cityName}</th>
                  )}
                  {comparison.market3 && (
                    <th className="text-center p-3 font-medium">{comparison.market3.cityName}</th>
                  )}
                  <th className="text-center p-3 font-medium">Winner</th>
                </tr>
              </thead>
              <tbody>
                {/* Home Affordability Score */}
                {userIncome > 0 && (
                  <tr className="border-b">
                    <td className="p-3 font-medium text-gray-700">Home Affordability Score</td>
                    {comparison.market1 && (
                      <td className="p-3 text-center">{getMarketScore(comparison.market1)?.toFixed(1)}/10</td>
                    )}
                    {comparison.market2 && (
                      <td className="p-3 text-center">{getMarketScore(comparison.market2)?.toFixed(1)}/10</td>
                    )}
                    {comparison.market3 && (
                      <td className="p-3 text-center">{getMarketScore(comparison.market3)?.toFixed(1)}/10</td>
                    )}
                    <td className="p-3 text-center">🏆</td>
                  </tr>
                )}
                
                {/* Starter Home Price */}
                <tr className="border-b" style={{ backgroundColor: '#f3f4f6' }}>
                  <td className="p-3 font-medium text-gray-700">Starter Home Price</td>
                  {comparison.market1 && (
                    <td className="p-3 text-center">{formatCurrency(comparison.market1.starterHomePrice)}</td>
                  )}
                  {comparison.market2 && (
                    <td className="p-3 text-center">{formatCurrency(comparison.market2.starterHomePrice)}</td>
                  )}
                  {comparison.market3 && (
                    <td className="p-3 text-center">{formatCurrency(comparison.market3.starterHomePrice)}</td>
                  )}
                  <td className="p-3 text-center">
                    {(() => {
                      const markets = [comparison.market1, comparison.market2, comparison.market3].filter(Boolean);
                      const lowest = markets.reduce((min, market) => 
                        market && (!min || market.starterHomePrice < min.starterHomePrice) ? market : min
                      );
                      return lowest ? `${lowest.cityName} 🏆` : '';
                    })()}
                  </td>
                </tr>
                
                {/* Property Tax Rate */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700">Property Tax Rate</td>
                  {comparison.market1 && (
                    <td className="p-3 text-center">{formatPercentage(comparison.market1.propertyTaxRate)}</td>
                  )}
                  {comparison.market2 && (
                    <td className="p-3 text-center">{formatPercentage(comparison.market2.propertyTaxRate)}</td>
                  )}
                  {comparison.market3 && (
                    <td className="p-3 text-center">{formatPercentage(comparison.market3.propertyTaxRate)}</td>
                  )}
                  <td className="p-3 text-center">
                    {(() => {
                      const markets = [comparison.market1, comparison.market2, comparison.market3].filter(Boolean);
                      const lowest = markets.reduce((min, market) => 
                        market && (!min || market.propertyTaxRate < min.propertyTaxRate) ? market : min
                      );
                      return lowest ? `${lowest.cityName} 🏆` : '';
                    })()}
                  </td>
                </tr>
                
                {/* Trade Wage Premium */}
                <tr className="border-b" style={{ backgroundColor: '#f3f4f6' }}>
                  <td className="p-3 font-medium text-gray-700">Trade Wages vs National Avg</td>
                  {comparison.market1 && (
                    <td className="p-3 text-center" style={{
                      color: comparison.market1.tradeSalaryPremium > 0.02 ? '#16a34a' : 
                             comparison.market1.tradeSalaryPremium < -0.02 ? '#dc2626' : 
                             '#6b7280'
                    }}>
                      {comparison.market1.tradeSalaryPremium >= 0 ? '+' : ''}{formatPercentage(comparison.market1.tradeSalaryPremium)}
                    </td>
                  )}
                  {comparison.market2 && (
                    <td className="p-3 text-center" style={{
                      color: comparison.market2.tradeSalaryPremium > 0.02 ? '#16a34a' : 
                             comparison.market2.tradeSalaryPremium < -0.02 ? '#dc2626' : 
                             '#6b7280'
                    }}>
                      {comparison.market2.tradeSalaryPremium >= 0 ? '+' : ''}{formatPercentage(comparison.market2.tradeSalaryPremium)}
                    </td>
                  )}
                  {comparison.market3 && (
                    <td className="p-3 text-center" style={{
                      color: comparison.market3.tradeSalaryPremium > 0.02 ? '#16a34a' : 
                             comparison.market3.tradeSalaryPremium < -0.02 ? '#dc2626' : 
                             '#6b7280'
                    }}>
                      {comparison.market3.tradeSalaryPremium >= 0 ? '+' : ''}{formatPercentage(comparison.market3.tradeSalaryPremium)}
                    </td>
                  )}
                  <td className="p-3 text-center">
                    {(() => {
                      const markets = [comparison.market1, comparison.market2, comparison.market3].filter(Boolean);
                      const highest = markets.reduce((max, market) => 
                        market && (!max || market.tradeSalaryPremium > max.tradeSalaryPremium) ? market : max
                      );
                      return highest ? `${highest.cityName} 🏆` : '';
                    })()}
                  </td>
                </tr>
                
                {/* Cost of Living Index */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700">Cost of Living Index</td>
                  {comparison.market1 && (
                    <td className="p-3 text-center">{comparison.market1.costOfLivingIndex.toFixed(2)}</td>
                  )}
                  {comparison.market2 && (
                    <td className="p-3 text-center">{comparison.market2.costOfLivingIndex.toFixed(2)}</td>
                  )}
                  {comparison.market3 && (
                    <td className="p-3 text-center">{comparison.market3.costOfLivingIndex.toFixed(2)}</td>
                  )}
                  <td className="p-3 text-center">
                    {(() => {
                      const markets = [comparison.market1, comparison.market2, comparison.market3].filter(Boolean);
                      const lowest = markets.reduce((min, market) => 
                        market && (!min || market.costOfLivingIndex < min.costOfLivingIndex) ? market : min
                      );
                      return lowest ? `${lowest.cityName} 🏆` : '';
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Sources Note */}
      <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-medium text-blue-900 mb-1">Data Sources:</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Zillow Home Value Index (ZHVI) for 25th percentile starter homes</li>
          <li>• Tax-Rates.org for property tax rates</li>
          <li>• Bureau of Labor Statistics Metropolitan Area data for trade wages</li>
          <li>• State tax rates from official state revenue departments</li>
        </ul>
      </div>
    </div>
  );
};

export default MarketComparison;